import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { httpClient } from '@/configs/axios.config'
import { companiesKeys, adminKeys } from '@/hooks/query-keys'
import type { OgsmAccess } from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'

// =============================================================================
// Types
// =============================================================================

interface CompanyAssignment {
  id: string
  companyId: string
  companyName: string
  assignmentType: string
  isActive: boolean
}

export interface AdminUser {
  id: string
  name: string
  email: string
  userType: string
  role: string
  allowedApps: AllowedApp[]
  isActive: boolean
  createdAt: string
  companyAssignments: CompanyAssignment[]
  /** Stored level; null for PortCo users; absent on a backend without GSCBE-141. */
  ogsmAccess?: OgsmAccess | null
  /** Effective right (admins always true); null for PortCo users. */
  ogsmCanEdit?: boolean | null
}

interface AdminUserListResponse {
  data: AdminUser[]
  nextCursor: string | null
  hasMore: boolean
}

type AdminUsersInfiniteData = InfiniteData<AdminUserListResponse, string | null>

export interface InternalInvitation {
  id: string
  emailAddress: string
  status: string
  createdAt: number
  invitedRole: string | null
  allowedApps: AllowedApp[]
  isConfigured: boolean
  /** Invited level; the backend reports EDIT when the invitation carries none. */
  ogsmAccess?: OgsmAccess | null
}

interface InternalInvitationListResponse {
  data: InternalInvitation[]
}

export interface AllowedDomain {
  id: string
  companyId: string
  domain: string
  createdAt: string
}

// Re-export adminKeys for consumers that import from this file
export { adminKeys } from '@/hooks/query-keys'

// =============================================================================
// API Functions
// =============================================================================

async function fetchAdminUsers(
  limit = 100,
  cursor?: string
): Promise<AdminUserListResponse> {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  if (cursor) params.set('cursor', cursor)
  const response = await httpClient.get<AdminUserListResponse>(
    `/users?${params.toString()}`
  )
  return response.data
}

// skipErrorToast: the only caller (EditGreybullUserSheet) shows the error inline.
async function changeUserRole(
  userId: string,
  role: string
): Promise<AdminUser> {
  const response = await httpClient.patch<AdminUser>(
    `/users/${userId}/role`,
    { role },
    { skipErrorToast: true }
  )
  return response.data
}

async function createCompanyAssignment(
  userId: string,
  companyId: string,
  assignmentType: string
): Promise<AdminUser> {
  const response = await httpClient.post<AdminUser>(
    `/users/${userId}/company-assignments`,
    { companyId, assignmentType }
  )
  return response.data
}

async function deleteCompanyAssignment(
  userId: string,
  companyId: string
): Promise<void> {
  await httpClient.delete(`/users/${userId}/company-assignments/${companyId}`)
}

async function inviteInternalUser(
  email: string,
  role?: string,
  allowedApps?: AllowedApp[],
  ogsmAccess?: OgsmAccess
): Promise<{ email: string; status: string }> {
  const response = await httpClient.post<{
    email: string
    status: string
  }>('/users/invite', {
    email,
    role,
    allowedApps,
    ...(ogsmAccess ? { ogsmAccess } : {}),
  })
  return response.data
}

async function fetchInternalInvitations(): Promise<InternalInvitationListResponse> {
  const response =
    await httpClient.get<InternalInvitationListResponse>('/users/invitations')
  return response.data
}

async function revokeInternalInvitation(invitationId: string): Promise<void> {
  await httpClient.delete(`/users/invitations/${invitationId}`)
}

async function resendInternalInvitation(
  invitationId: string
): Promise<{ email: string; status: 'resent' }> {
  const response = await httpClient.post<{ email: string; status: 'resent' }>(
    `/users/invitations/${invitationId}/resend`
  )
  return response.data
}

// skipErrorToast: the only caller (EditGreybullUserSheet) shows the error inline.
async function updateUserAccess(
  userId: string,
  allowedApps: AllowedApp[]
): Promise<AdminUser> {
  const response = await httpClient.patch<AdminUser>(
    `/users/${userId}/access`,
    { allowedApps },
    { skipErrorToast: true }
  )
  return response.data
}

async function changeInternalUserOgsmAccess(
  userId: string,
  ogsmAccess: OgsmAccess
): Promise<AdminUser> {
  const response = await httpClient.patch<AdminUser>(
    `/users/${userId}/ogsm-access`,
    { ogsmAccess },
    { skipErrorToast: true }
  )
  return response.data
}

async function fetchAllowedDomains(
  companyId: string
): Promise<AllowedDomain[]> {
  const response = await httpClient.get<AllowedDomain[]>(
    `/companies/${companyId}/allowed-domains`
  )
  return response.data
}

async function createAllowedDomain(
  companyId: string,
  domain: string
): Promise<AllowedDomain> {
  const response = await httpClient.post<AllowedDomain>(
    `/companies/${companyId}/allowed-domains`,
    { domain }
  )
  return response.data
}

async function deleteAllowedDomain(
  companyId: string,
  domainId: string
): Promise<void> {
  await httpClient.delete(`/companies/${companyId}/allowed-domains/${domainId}`)
}

// =============================================================================
// Query Hooks
// =============================================================================

export function useAdminUsers() {
  const queryKey = adminKeys.usersList()
  const query = useInfiniteQuery<
    AdminUserListResponse,
    Error,
    AdminUser[],
    typeof queryKey,
    string | null
  >({
    queryKey,
    queryFn: ({ pageParam }) => fetchAdminUsers(100, pageParam ?? undefined),
    initialPageParam: null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    select: (data) => data.pages.flatMap((page) => page.data),
    staleTime: 2 * 60 * 1000,
  })

  const {
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
  } = query

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage])

  return {
    ...query,
    isLoading:
      !query.isError &&
      (query.isLoading || query.hasNextPage || query.isFetchingNextPage),
  }
}

export function useInternalInvitations() {
  return useQuery({
    queryKey: adminKeys.invitations(),
    queryFn: fetchInternalInvitations,
    staleTime: 2 * 60 * 1000,
  })
}

export function useAllowedDomains(companyId: string, enabled = true) {
  return useQuery({
    queryKey: adminKeys.domainsByCompany(companyId),
    queryFn: () => fetchAllowedDomains(companyId),
    enabled: enabled && Boolean(companyId),
    staleTime: 2 * 60 * 1000,
  })
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useChangeUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      changeUserRole(userId, role),
    onMutate: async (variables) => {
      const key = adminKeys.usersList()
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AdminUsersInfiniteData>(key)
      queryClient.setQueryData<AdminUsersInfiniteData>(key, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: page.data.map((user) =>
                  user.id === variables.userId
                    ? { ...user, role: variables.role }
                    : user
                ),
              })),
            }
          : old
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCreateCompanyAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      assignmentType,
    }: {
      userId: string
      companyId: string
      companyName?: string
      assignmentType: string
    }) => createCompanyAssignment(userId, companyId, assignmentType),
    onMutate: async (variables) => {
      const key = adminKeys.usersList()
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AdminUsersInfiniteData>(key)
      queryClient.setQueryData<AdminUsersInfiniteData>(key, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((user) => {
              if (user.id !== variables.userId) return user
              const optimisticAssignment: CompanyAssignment = {
                id: `optimistic-${Date.now()}`,
                companyId: variables.companyId,
                companyName: variables.companyName ?? 'Loading…',
                assignmentType: variables.assignmentType,
                isActive: true,
              }
              return {
                ...user,
                companyAssignments: [
                  ...user.companyAssignments,
                  optimisticAssignment,
                ],
              }
            }),
          })),
        }
      })
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
    },
  })
}

export function useDeleteCompanyAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      companyId,
    }: {
      userId: string
      companyId: string
    }) => deleteCompanyAssignment(userId, companyId),
    onMutate: async (variables) => {
      const key = adminKeys.usersList()
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AdminUsersInfiniteData>(key)
      queryClient.setQueryData<AdminUsersInfiniteData>(key, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((user) =>
              user.id === variables.userId
                ? {
                    ...user,
                    companyAssignments: user.companyAssignments.filter(
                      (assignment) =>
                        assignment.companyId !== variables.companyId
                    ),
                  }
                : user
            ),
          })),
        }
      })
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: companiesKeys.all })
    },
  })
}

export function useCreateAllowedDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      domain,
    }: {
      companyId: string
      domain: string
    }) => createAllowedDomain(companyId, domain),
    onMutate: async (variables) => {
      const key = adminKeys.domainsByCompany(variables.companyId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AllowedDomain[]>(key)
      const optimistic: AllowedDomain = {
        id: `optimistic-${Date.now()}`,
        companyId: variables.companyId,
        domain: variables.domain,
        createdAt: new Date().toISOString(),
      }
      queryClient.setQueryData<AllowedDomain[]>(key, (old) =>
        old ? [...old, optimistic] : [optimistic]
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.domainsByCompany(variables.companyId),
      })
    },
  })
}

export function useDeleteAllowedDomain() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      companyId,
      domainId,
    }: {
      companyId: string
      domainId: string
    }) => deleteAllowedDomain(companyId, domainId),
    onMutate: async (variables) => {
      const key = adminKeys.domainsByCompany(variables.companyId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AllowedDomain[]>(key)
      queryClient.setQueryData<AllowedDomain[]>(key, (old) =>
        old ? old.filter((d) => d.id !== variables.domainId) : old
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.domainsByCompany(variables.companyId),
      })
    },
  })
}

export function useInviteInternalUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      email,
      role,
      allowedApps,
      ogsmAccess,
    }: {
      email: string
      role?: string
      allowedApps: AllowedApp[]
      ogsmAccess?: OgsmAccess
    }) => inviteInternalUser(email, role, allowedApps, ogsmAccess),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.invitations() })
    },
  })
}

export function useRevokeInternalInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: revokeInternalInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.invitations() })
    },
  })
}

export function useResendInternalInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resendInternalInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.invitations() })
    },
  })
}

export function useUpdateUserAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      allowedApps,
    }: {
      userId: string
      allowedApps: AllowedApp[]
    }) => updateUserAccess(userId, allowedApps),
    onMutate: async (variables) => {
      const key = adminKeys.usersList()
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<AdminUsersInfiniteData>(key)
      queryClient.setQueryData<AdminUsersInfiniteData>(key, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: page.data.map((user) =>
                  user.id === variables.userId
                    ? {
                        ...user,
                        allowedApps: variables.allowedApps,
                      }
                    : user
                ),
              })),
            }
          : old
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useChangeInternalUserOgsmAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      ogsmAccess,
    }: {
      userId: string
      ogsmAccess: OgsmAccess
    }) => changeInternalUserOgsmAccess(userId, ogsmAccess),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}
